import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import {
  ClientRegisteredVendor,
  ExemptVendor,
  VendorRanking
} from '../classes/models';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ExemptVendorService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  public getExemptVendor(query: {} = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/exemptVendor`, { params: query })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveExemptVendor(
    ExemptVendor: ExemptVendor | {},
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/saveExemptVendor`,
              ExemptVendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/exemptVendor/saveExemptVendor`,
              ExemptVendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveVendorRank(
    ExemptVendorId: number,
    VendorRank: VendorRanking,
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.put(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/${ExemptVendorId}/setVendorRank`,
              VendorRank,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public checkForDuplicateTaxId(
    exemptVendorRecord: ExemptVendor | {},
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/checkForDuplicateTaxId`,
              exemptVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/exemptVendor/checkForDuplicateTaxId`,
              exemptVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public checkForDuplicateVendorTaxId(
    exemptVendorRecord: ExemptVendor | {},
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/checkForDuplicateVendorTaxId`,
              exemptVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/exemptVendor/checkForDuplicateVendorTaxId`,
              exemptVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public checkForDuplicateLegalName(
    exemptVendorRecord: ExemptVendor | {},
    query: {} = {}
  ): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/checkForDuplicateLegalName`,
              exemptVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/exemptVendor/checkForDuplicateLegalName`,
              exemptVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public convertVendorToClientRegisteredVendor(
    query: {} = {}
  ): Observable<ClientRegisteredVendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<ClientRegisteredVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendor/convertVendorToClientRegisteredVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<ClientRegisteredVendor>(
              `/api/v2/admin/exemptVendor/convertVendorToClientRegisteredVendor`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  getExemptVendorTypes(query = {}): Observable<any> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/exemptVendorType/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get(`/api/v2/admin/exemptVendorType/many`, {
              params: query
            })
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
